// /frontend/src/pages/admin/AdminSettings.jsx
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { FaSave, FaStore, FaShippingFast, FaCreditCard, FaEnvelope, FaGlobe } from 'react-icons/fa';
import { settingService } from '../../services/settingService';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  
  // Settings state
  const [generalSettings, setGeneralSettings] = useState({
    storeName: '',
    storeEmail: '',
    phoneNumber: '',
    address: '',
    logo: null,
    favicon: null
  });
  
  const [shippingSettings, setShippingSettings] = useState({
    enableFreeShipping: false,
    freeShippingThreshold: '',
    flatRateShipping: '',
    allowLocalPickup: false,
    localPickupAddress: '',
    allowInternationalShipping: false,
    internationalShippingRate: ''
  });
  
  const [paymentSettings, setPaymentSettings] = useState({
    allowCreditCard: true,
    allowPaypal: false,
    allowBankTransfer: false,
    bankDetails: '',
    allowCashOnDelivery: false,
    taxRate: ''
  });
  
  const [emailSettings, setEmailSettings] = useState({
    orderConfirmationTemplate: '',
    shippingConfirmationTemplate: '',
    welcomeEmailTemplate: '',
    notifyLowStock: false,
    lowStockThreshold: '',
    adminEmail: ''
  });
  
  const [seoSettings, setSeoSettings] = useState({
    metaTitle: '',
    metaDescription: '',
    ogImage: null,
    siteKeywords: '',
    googleAnalyticsId: '',
    enableSitemap: true
  });
  
  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await settingService.getSettings();
        
        // Set each settings group
        setGeneralSettings(data.general || generalSettings);
        setShippingSettings(data.shipping || shippingSettings);
        setPaymentSettings(data.payment || paymentSettings);
        setEmailSettings(data.email || emailSettings);
        setSeoSettings(data.seo || seoSettings);
        
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to fetch settings');
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  // Handle input change for different setting groups
  const handleGeneralChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      setGeneralSettings({
        ...generalSettings,
        [name]: files[0]
      });
    } else {
      setGeneralSettings({
        ...generalSettings,
        [name]: value
      });
    }
  };
  
  const handleShippingChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setShippingSettings({
      ...shippingSettings,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handlePaymentChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setPaymentSettings({
      ...paymentSettings,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleEmailChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setEmailSettings({
      ...emailSettings,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSeoChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    
    if (type === 'file') {
      setSeoSettings({
        ...seoSettings,
        [name]: files[0]
      });
    } else {
      setSeoSettings({
        ...seoSettings,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };
  
  // Save settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Create FormData object to handle file uploads
      const formData = new FormData();
      
      // Add each settings group as JSON (excluding file fields)
      const generalWithoutFiles = { ...generalSettings };
      delete generalWithoutFiles.logo;
      delete generalWithoutFiles.favicon;
      
      const seoWithoutFiles = { ...seoSettings };
      delete seoWithoutFiles.ogImage;
      
      formData.append('general', JSON.stringify(generalWithoutFiles));
      formData.append('shipping', JSON.stringify(shippingSettings));
      formData.append('payment', JSON.stringify(paymentSettings));
      formData.append('email', JSON.stringify(emailSettings));
      formData.append('seo', JSON.stringify(seoWithoutFiles));
      
      // Add file fields
      if (generalSettings.logo instanceof File) {
        formData.append('logo', generalSettings.logo);
      }
      
      if (generalSettings.favicon instanceof File) {
        formData.append('favicon', generalSettings.favicon);
      }
      
      if (seoSettings.ogImage instanceof File) {
        formData.append('ogImage', seoSettings.ogImage);
      }
      
      await settingService.updateSettings(formData);
      
      setSaving(false);
      alert('Settings saved successfully');
    } catch (err) {
      setError(err.message || 'Failed to save settings');
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-800"></div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Store Settings</h1>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className={`flex items-center px-4 py-2 rounded-md text-white ${
            saving ? 'bg-teal-600 cursor-wait' : 'bg-teal-600 hover:bg-teal-700'
          }`}
        >
          {saving ? (
            <>
              <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              Saving...
            </>
          ) : (
            <>
              <FaSave className="mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md mb-6">
          <p>{error}</p>
        </div>
      )}
      
      {/* Settings Tabs */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('general')}
              className={`px-6 py-3 text-sm font-medium flex items-center ${
                activeTab === 'general'
                  ? 'border-b-2 border-teal-500 text-teal-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaStore className="mr-2" />
              General
            </button>
            <button
              onClick={() => setActiveTab('shipping')}
              className={`px-6 py-3 text-sm font-medium flex items-center ${
                activeTab === 'shipping'
                  ? 'border-b-2 border-teal-500 text-teal-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaShippingFast className="mr-2" />
              Shipping
            </button>
            <button
              onClick={() => setActiveTab('payment')}
              className={`px-6 py-3 text-sm font-medium flex items-center ${
                activeTab === 'payment'
                  ? 'border-b-2 border-teal-500 text-teal-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaCreditCard className="mr-2" />
              Payment
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`px-6 py-3 text-sm font-medium flex items-center ${
                activeTab === 'email'
                  ? 'border-b-2 border-teal-500 text-teal-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaEnvelope className="mr-2" />
              Email
            </button>
            <button
              onClick={() => setActiveTab('seo')}
              className={`px-6 py-3 text-sm font-medium flex items-center ${
                activeTab === 'seo'
                  ? 'border-b-2 border-teal-500 text-teal-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaGlobe className="mr-2" />
              SEO
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">General Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 mb-1">
                    Store Name
                  </label>
                  <input
                    type="text"
                    id="storeName"
                    name="storeName"
                    value={generalSettings.storeName}
                    onChange={handleGeneralChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Your Store Name"
                  />
                </div>
                
                <div>
                  <label htmlFor="storeEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    Store Email
                  </label>
                  <input
                    type="email"
                    id="storeEmail"
                    name="storeEmail"
                    value={generalSettings.storeEmail}
                    onChange={handleGeneralChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="store@example.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={generalSettings.phoneNumber}
                    onChange={handleGeneralChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="+1 (123) 456-7890"
                  />
                </div>
                
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                    Store Address
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={generalSettings.address}
                    onChange={handleGeneralChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="123 Main St, City, State, ZIP"
                  ></textarea>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="logo" className="block text-sm font-medium text-gray-700 mb-1">
                    Store Logo
                  </label>
                  <input
                    type="file"
                    id="logo"
                    name="logo"
                    onChange={handleGeneralChange}
                    accept="image/*"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  {generalSettings.logoUrl && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-1">Current Logo:</p>
                      <img 
                        src={generalSettings.logoUrl} 
                        alt="Store Logo" 
                        className="h-12 object-contain"
                      />
                    </div>
                  )}
                </div>
                
                <div>
                  <label htmlFor="favicon" className="block text-sm font-medium text-gray-700 mb-1">
                    Favicon
                  </label>
                  <input
                    type="file"
                    id="favicon"
                    name="favicon"
                    onChange={handleGeneralChange}
                    accept="image/x-icon,image/png"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: 32x32 PNG or ICO file
                  </p>
                  {generalSettings.faviconUrl && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-1">Current Favicon:</p>
                      <img 
                        src={generalSettings.faviconUrl} 
                        alt="Favicon" 
                        className="h-8 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Shipping Settings Tab */}
          {activeTab === 'shipping' && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Shipping Settings</h2>
              
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="enableFreeShipping"
                    name="enableFreeShipping"
                    checked={shippingSettings.enableFreeShipping}
                    onChange={handleShippingChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="enableFreeShipping" className="ml-2 text-sm text-gray-700">
                    Enable Free Shipping
                  </label>
                </div>
                
                {shippingSettings.enableFreeShipping && (
                  <div className="ml-6 mb-4">
                    <label htmlFor="freeShippingThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                      Free Shipping Threshold
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">$</span>
                      <input
                        type="number"
                        id="freeShippingThreshold"
                        name="freeShippingThreshold"
                        value={shippingSettings.freeShippingThreshold}
                        onChange={handleShippingChange}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="50.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Orders above this amount qualify for free shipping
                    </p>
                  </div>
                )}
                
                <div className="mb-4">
                  <label htmlFor="flatRateShipping" className="block text-sm font-medium text-gray-700 mb-1">
                    Flat Rate Shipping Cost
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">$</span>
                    <input
                      type="number"
                      id="flatRateShipping"
                      name="flatRateShipping"
                      value={shippingSettings.flatRateShipping}
                      onChange={handleShippingChange}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="5.99"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="allowLocalPickup"
                    name="allowLocalPickup"
                    checked={shippingSettings.allowLocalPickup}
                    onChange={handleShippingChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="allowLocalPickup" className="ml-2 text-sm text-gray-700">
                    Allow Local Pickup
                  </label>
                </div>
                
                {shippingSettings.allowLocalPickup && (
                  <div className="ml-6 mb-4">
                    <label htmlFor="localPickupAddress" className="block text-sm font-medium text-gray-700 mb-1">
                      Local Pickup Address
                    </label>
                    <textarea
                      id="localPickupAddress"
                      name="localPickupAddress"
                      value={shippingSettings.localPickupAddress}
                      onChange={handleShippingChange}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="123 Main St, City, State, ZIP"
                    ></textarea>
                  </div>
                )}
                
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="allowInternationalShipping"
                    name="allowInternationalShipping"
                    checked={shippingSettings.allowInternationalShipping}
                    onChange={handleShippingChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="allowInternationalShipping" className="ml-2 text-sm text-gray-700">
                    Allow International Shipping
                  </label>
                </div>
                
                {shippingSettings.allowInternationalShipping && (
                  <div className="ml-6 mb-4">
                    <label htmlFor="internationalShippingRate" className="block text-sm font-medium text-gray-700 mb-1">
                      International Shipping Rate
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">$</span>
                      <input
                        type="number"
                        id="internationalShippingRate"
                        name="internationalShippingRate"
                        value={shippingSettings.internationalShippingRate}
                        onChange={handleShippingChange}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="25.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Payment Settings Tab */}
          {activeTab === 'payment' && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Payment Settings</h2>
              
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Payment Methods</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allowCreditCard"
                      name="allowCreditCard"
                      checked={paymentSettings.allowCreditCard}
                      onChange={handlePaymentChange}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <label htmlFor="allowCreditCard" className="ml-2 text-sm text-gray-700">
                      Allow Credit Card Payment
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allowPaypal"
                      name="allowPaypal"
                      checked={paymentSettings.allowPaypal}
                      onChange={handlePaymentChange}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <label htmlFor="allowPaypal" className="ml-2 text-sm text-gray-700">
                      Allow PayPal Payment
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allowBankTransfer"
                      name="allowBankTransfer"
                      checked={paymentSettings.allowBankTransfer}
                      onChange={handlePaymentChange}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <label htmlFor="allowBankTransfer" className="ml-2 text-sm text-gray-700">
                      Allow Bank Transfer
                    </label>
                  </div>
                  
                  {paymentSettings.allowBankTransfer && (
                    <div className="ml-6 mb-4">
                      <label htmlFor="bankDetails" className="block text-sm font-medium text-gray-700 mb-1">
                        Bank Details
                      </label>
                      <textarea
                        id="bankDetails"
                        name="bankDetails"
                        value={paymentSettings.bankDetails}
                        onChange={handlePaymentChange}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Bank name, Account number, etc."
                      ></textarea>
                    </div>
                  )}
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allowCashOnDelivery"
                      name="allowCashOnDelivery"
                      checked={paymentSettings.allowCashOnDelivery}
                      onChange={handlePaymentChange}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <label htmlFor="allowCashOnDelivery" className="ml-2 text-sm text-gray-700">
                      Allow Cash on Delivery
                    </label>
                  </div>
                </div>
              </div>
              
              <div>
                <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 mb-1">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  id="taxRate"
                  name="taxRate"
                  value={paymentSettings.taxRate}
                  onChange={handlePaymentChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="7.5"
                  min="0"
                  max="100"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter tax rate as a percentage (e.g. 7.5 for 7.5%)
                </p>
              </div>
            </div>
          )}
          
          {/* Email Settings Tab */}
          {activeTab === 'email' && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Email Settings</h2>
              
              <div className="mb-6">
                <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Notification Email
                </label>
                <input
                  type="email"
                  id="adminEmail"
                  name="adminEmail"
                  value={emailSettings.adminEmail}
                  onChange={handleEmailChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="admin@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email address for order notifications and system alerts
                </p>
              </div>
              
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Email Templates</h3>
                
                <div className="mb-4">
                  <label htmlFor="orderConfirmationTemplate" className="block text-sm font-medium text-gray-700 mb-1">
                    Order Confirmation Email
                  </label>
                  <textarea
                    id="orderConfirmationTemplate"
                    name="orderConfirmationTemplate"
                    value={emailSettings.orderConfirmationTemplate}
                    onChange={handleEmailChange}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Email template content..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    Use {'{{order_id}}'}, {'{{customer_name}}'}, etc. as placeholders
                  </p>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="shippingConfirmationTemplate" className="block text-sm font-medium text-gray-700 mb-1">
                    Shipping Confirmation Email
                  </label>
                  <textarea
                    id="shippingConfirmationTemplate"
                    name="shippingConfirmationTemplate"
                    value={emailSettings.shippingConfirmationTemplate}
                    onChange={handleEmailChange}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Email template content..."
                  ></textarea>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="welcomeEmailTemplate" className="block text-sm font-medium text-gray-700 mb-1">
                    Welcome Email
                  </label>
                  <textarea
                    id="welcomeEmailTemplate"
                    name="welcomeEmailTemplate"
                    value={emailSettings.welcomeEmailTemplate}
                    onChange={handleEmailChange}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Email template content..."
                  ></textarea>
                </div>
              </div>
              
              <div>
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="notifyLowStock"
                    name="notifyLowStock"
                    checked={emailSettings.notifyLowStock}
                    onChange={handleEmailChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="notifyLowStock" className="ml-2 text-sm text-gray-700">
                    Email Notifications for Low Stock
                  </label>
                </div>
                
                {emailSettings.notifyLowStock && (
                  <div className="ml-6">
                    <label htmlFor="lowStockThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                      Low Stock Threshold
                    </label>
                    <input
                      type="number"
                      id="lowStockThreshold"
                      name="lowStockThreshold"
                      value={emailSettings.lowStockThreshold}
                      onChange={handleEmailChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="5"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Send notifications when stock falls below this level
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* SEO Settings Tab */}
          {activeTab === 'seo' && (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">SEO Settings</h2>
              
              <div className="mb-4">
                <label htmlFor="metaTitle" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Meta Title
                </label>
                <input
                  type="text"
                  id="metaTitle"
                  name="metaTitle"
                  value={seoSettings.metaTitle}
                  onChange={handleSeoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Your Store Name - Tagline"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="metaDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Meta Description
                </label>
                <textarea
                  id="metaDescription"
                  name="metaDescription"
                  value={seoSettings.metaDescription}
                  onChange={handleSeoChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="A brief description of your store (150-160 characters recommended)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  Recommended: 150-160 characters
                </p>
              </div>
              
              <div className="mb-4">
                <label htmlFor="siteKeywords" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Keywords
                </label>
                <input
                  type="text"
                  id="siteKeywords"
                  name="siteKeywords"
                  value={seoSettings.siteKeywords}
                  onChange={handleSeoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="keyword1, keyword2, keyword3"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Comma-separated keywords
                </p>
              </div>
              
              <div className="mb-4">
                <label htmlFor="ogImage" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Social Media Image
                </label>
                <input
                  type="file"
                  id="ogImage"
                  name="ogImage"
                  onChange={handleSeoChange}
                  accept="image/*"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recommended: 1200 x 630 pixels
                </p>
                {seoSettings.ogImageUrl && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-1">Current OG Image:</p>
                    <img 
                      src={seoSettings.ogImageUrl} 
                      alt="OG Image" 
                      className="h-20 object-contain"
                    />
                  </div>
                )}
              </div>
              
              <div className="mb-4">
                <label htmlFor="googleAnalyticsId" className="block text-sm font-medium text-gray-700 mb-1">
                  Google Analytics ID
                </label>
                <input
                  type="text"
                  id="googleAnalyticsId"
                  name="googleAnalyticsId"
                  value={seoSettings.googleAnalyticsId}
                  onChange={handleSeoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="UA-XXXXXXXXX-X or G-XXXXXXXXXX"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableSitemap"
                  name="enableSitemap"
                  checked={seoSettings.enableSitemap}
                  onChange={handleSeoChange}
                  className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <label htmlFor="enableSitemap" className="ml-2 text-sm text-gray-700">
                  Generate XML Sitemap
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;